import { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { I18n } from '@aws-amplify/core';

import type { CycleCallback } from '@/components/ui/Timeline';
import type { BeraReportV2 } from '@/types/Bera';
import { toStretches, toStretchesFromReports } from '@/components/views/BeraJob/BeraReportV2/utils';

import {
	buildStretch,
	hasStretchMoved,
	clampStretchToVideo,
	findNextFreeSlot,
	keyStretchesById,
	sortStretchesChronologically
} from '@/components/views/BeraJob/BeraReportV2/Timeline/helpers';

interface UseStretchDraftParams {
	reports: BeraReportV2[];
	videoDuration: number;
	selectedReport?: BeraReportV2;
}

function notifyStretchAdjusted(): void {
	message.warning(I18n.get('Stretch adjusted to the end of the video'));
}

function notifyStretchRefused(): void {
	message.error(I18n.get('Enter a start before the end of the video'));
}

export function useStretchDraft({ reports, videoDuration, selectedReport }: UseStretchDraftParams) {
	const [stretches, setStretches] = useState<CycleCallback[]>([]);
	const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(undefined);
	const [rulerEdits, setRulerEdits] = useState<Record<string, CycleCallback>>({});

	const savedStretches = useMemo(() => toStretchesFromReports(reports), [reports]);

	const savedStretchById = useMemo(() => keyStretchesById(savedStretches), [savedStretches]);

	const listedStretches = useMemo(() => {
		const fromSelectedTask = stretches.filter(
			(stretch) => !stretch.identified || stretch.type_id === selectedTaskId
		);
		const result = sortStretchesChronologically(fromSelectedTask);

		return result;
	}, [stretches, selectedTaskId]);

	const rulerStretches = useMemo(() => {
		const fromOtherTlids = savedStretches
			.filter((stretch) => stretch.type_id !== selectedTaskId)
			.map((stretch) => rulerEdits[stretch.id] ?? stretch);
		const result = fromOtherTlids.concat(stretches);

		return result;
	}, [savedStretches, stretches, selectedTaskId, rulerEdits]);

	const nextFreeSlot = useMemo(
		() => findNextFreeSlot(rulerStretches, videoDuration),
		[rulerStretches, videoDuration]
	);

	const hasUnsavedStretch = stretches.some((stretch) => !stretch.identified);

	useEffect(() => {
		if (!selectedReport) {
			return;
		}

		setSelectedTaskId(selectedReport.task_id);

		setRulerEdits({});

		setStretches((current) => {
			const unsaved = current.filter((stretch) => !stretch.identified);
			const fromReport = toStretches(selectedReport.segments ?? [], selectedReport.task_id);
			const result = fromReport.concat(unsaved);

			return result;
		});
	}, [selectedReport]);

	const selectTask = useCallback(
		(taskId: string) => {
			setSelectedTaskId(taskId);

			setRulerEdits({});

			setStretches((current) => {
				const unsaved = current.filter((stretch) => !stretch.identified);
				const fromTask = savedStretches.filter((stretch) => stretch.type_id === taskId);
				const result = fromTask.concat(unsaved);

				return result;
			});
		},
		[savedStretches]
	);

	const addStretch = useCallback(() => {
		if (nextFreeSlot === null) {
			return;
		}

		const stretch = buildStretch({ slot: nextFreeSlot, tlidId: selectedTaskId });

		setStretches((current) => current.concat(stretch));
	}, [nextFreeSlot, selectedTaskId]);

	const applyRulerChange = useCallback(
		(cycles: CycleCallback[]) => {
			function hasMoved(cycle: CycleCallback): boolean {
				const result = hasStretchMoved(cycle, savedStretchById[cycle.id]);

				return result;
			}

			const bufferIds = new Set(stretches.map((stretch) => stretch.id));
			const fromBuffer = cycles.filter((cycle) => bufferIds.has(cycle.id));
			const fromRuler = cycles.filter((cycle) => !bufferIds.has(cycle.id));
			const movedFromRuler = fromRuler.filter(hasMoved);
			const movedFromBuffer = fromBuffer.filter((cycle) => !!cycle.identified && hasMoved(cycle));
			const moved = movedFromRuler.concat(movedFromBuffer);

			setStretches(fromBuffer);
			setRulerEdits((current) => ({ ...current, ...keyStretchesById(moved) }));
		},
		[stretches, savedStretchById]
	);

	const changeStretch = useCallback(
		(updated: CycleCallback) => {
			const clamped = clampStretchToVideo(updated, videoDuration);

			if (!clamped) {
				notifyStretchRefused();
				return;
			}

			if (clamped !== updated) {
				notifyStretchAdjusted();
			}

			setStretches((current) => current.map((stretch) => (stretch.id === clamped.id ? clamped : stretch)));
		},
		[videoDuration]
	);

	const removeStretch = useCallback((stretchId: string) => {
		setStretches((current) => current.filter((stretch) => stretch.id !== stretchId));
	}, []);

	function resetDraft(): void {
		setStretches([]);

		setSelectedTaskId(undefined);

		setRulerEdits({});
	}

	function dropSelectedTaskStretches(): void {
		setStretches((current) => current.filter((stretch) => stretch.type_id !== selectedTaskId));
	}

	function clearRulerEdits(): void {
		setRulerEdits({});
	}

	return {
		rulerEdits,
		selectedTaskId,
		rulerStretches,
		listedStretches,
		nextFreeSlot,
		hasUnsavedStretch,
		addStretch,
		changeStretch,
		removeStretch,
		resetDraft,
		selectTask,
		applyRulerChange,
		clearRulerEdits,
		dropSelectedTaskStretches
	};
}
